import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import { Collapse, Link, ListItemButton, ListItemIcon, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as ReactRouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import React from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import Icon from '@mui/material/Icon';
import { fetchUser } from '../../redux/slices/userSlice';
import Swal from 'sweetalert2';
import jwtDecode from 'jwt-decode';
import { getMenuOfRole } from '../../services/MenuService';
import menuOrder from "../../config/menuOrder"

const drawerWidth = 260;

interface RootObject {
  menu_id: string
  menu_name: string
  menu_name_en: string
  menu_name_th: string
  parent_id?: any
  icon?: any
  children: Child[]
  is_active: boolean
}

interface Child {
  children_id: string
  menu_name: string
  menu_name_en: string
  menu_name_th: string
  route_name?: string
  icon: string
  is_active: boolean
}

const AdminSidebar = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const path = location.pathname.replace(/\/$/, "")
  const pathParts = path.split("/")
  const dispatch = useAppDispatch()

  const [opened, setOpened] = useState<{ [key: string]: boolean }>({})
  const [hovered, setHovered] = useState<{ [key: string]: boolean }>({})
  const [activeMenu, setActiveMenu] = useState<{ [key: string]: boolean }>({})
  const [menusData, setMenusData] = useState<RootObject[]>([])
  const { roleid } = useAppSelector((state) => state.user)

  useEffect(() => {
    if (menusData.length > 0) {
      menusData.forEach((menu) => {
        if (menu.children.length > 0) {
          menu.children.forEach((child) => {
            if ("/admin/" + child.route_name?.split("?")[0] === path) {
              handleActiveMenu(child.menu_name)
            }
          })
        }
      })
    }
  }, [menusData, navigate])

  useEffect(() => {
    const fetchUserData = async () => {
      const userData = await dispatch(fetchUser())
      if (userData.payload) {
        if (
          userData.payload.role_id !==
          jwtDecode<any>(localStorage.getItem("access_token")!).RoleId
        ) {
          Swal.fire({
            title: `${t("Role Changed")}`,
            text: `${t("Your role has been changed. Please re-login again")}`,
            icon: "warning",
            confirmButtonText: `${t("ok")}`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showCancelButton: false,
            showCloseButton: false,
            showDenyButton: false,
          }).then((result) => {
            localStorage.removeItem("access_token")
            if (result.isConfirmed) {
              window.location.href = "/login"
            }
          })
        }
      }
    }
    fetchUserData()
  }, [navigate])

  const handleMouseEnter = (hoverName: string) => {
    setHovered((prev) => ({ ...prev, [hoverName]: true }))
  }

  const handleMouseLeave = (hoverName: string) => {
    setHovered((prev) => ({ ...prev, [hoverName]: false }))
  }

  const toggleOpen = (menuName: string) => {
    setOpened((prev) => ({ ...prev, [menuName]: !prev[menuName] }))
  }

  const handleActiveMenu = (menuName: string) => {
    Object.keys(activeMenu).forEach((key) => {
      activeMenu[key] = false
    })
    setActiveMenu((prev) => ({ ...prev, [menuName]: true }))
  }

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await getMenuOfRole({ roleId: roleid })
        if (response.data?.success) {
          const menus: RootObject[] = response.data?.menus ?? []

          const sorted = menus
            .filter((m) => !menuOrder[String(m.menu_id)]?.hidden)
            .sort((a, b) => {
              const idA = String(a.menu_id)
              const idB = String(b.menu_id)
              const configA = menuOrder[idA]
              const configB = menuOrder[idB]
              const orderA = configA ? configA.order : 9999 + menus.indexOf(a)
              const orderB = configB ? configB.order : 9999 + menus.indexOf(b)
              return orderA - orderB
            })

          setMenusData(sorted)
        }
      } catch {
        navigate("/servererror", { replace: true })
      }
    }
    fetchMenus()
  }, [])

  useEffect(() => {
    if (pathParts[2] === "masterdata") {
      setOpened((prev) => ({ ...prev, SystemAdministration: true }))
    } else if (pathParts[2] === "jobs") {
      setOpened((prev) => ({ ...prev, JobHiring: true }))
    } else if (pathParts[2] === "application-form") {
      setOpened((prev) => ({ ...prev, ApplicationForm: true }))
    } else if (pathParts[2] === "interview") {
      setOpened((prev) => ({ ...prev, Interview: true }))
    } else if (pathParts[2] === "dashboard" || pathParts[2] === undefined) {
      setOpened((prev) => ({ ...prev, Dashboard: true }))
      Object.keys(activeMenu).forEach((k) => {
        activeMenu[k] = false
      })
      setActiveMenu((prev) => ({ ...prev, Dashboard: true }))
    }
  }, [navigate])

  useEffect(() => {
    if (pathParts[3] !== undefined) {
      handleActiveMenu(pathParts[3].toLowerCase())
    }
  }, [])

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", sm: "block" },
        width: drawerWidth,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
        },
        zIndex: "1",
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: "auto", paddingX: 2 }}>
        <List>
          {menusData?.length > 0 &&
            menusData?.map((menu, index) => (
              <div key={index}>
                <Box
                  onClick={() => toggleOpen(menu?.menu_name)}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    width: "220px",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: "12px",
                      marginLeft: 2,
                      marginTop: 3,
                      marginBottom: 1,
                      fontWeight: "bold",
                      userSelect: "none",
                      textTransform: "uppercase",
                    }}
                  >
                    {t(menu?.menu_name)}
                    {/* {i18n.language === "th"
                      ? menu?.menu_name_th
                      : menu?.menu_name_en || t(menu?.menu_name)} */}
                  </Typography>
                  <Box>
                    {opened[menu?.menu_name] ? (
                      <Icon>expand_less</Icon>
                    ) : (
                      <Icon>expand_more</Icon>
                    )}
                  </Box>
                </Box>
                <Collapse
                  in={opened[menu?.menu_name]}
                  timeout="auto"
                  unmountOnExit
                >
                  {menu?.children.length > 0 ? (
                    <>
                      {menu?.children.map((child, childIndex) => (
                        <Link
                          key={childIndex}
                          component={ReactRouterLink}
                          to={`${child.route_name}`}
                          color="inherit"
                          underline="none"
                          onMouseEnter={() => handleMouseEnter(child.menu_name)}
                          onMouseLeave={() => handleMouseLeave(child.menu_name)}
                          onClick={() => handleActiveMenu(child.menu_name)}
                        >
                          <ListItemButton
                            sx={{
                              pl: 2,
                              marginBottom: 0.5,
                              borderRadius: 1,
                              backgroundColor: activeMenu[child.menu_name]
                                ? theme.palette.primary.main
                                : "inherit",
                              "&:hover": {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 0 }}>
                              <Icon
                                sx={{
                                  color:
                                    hovered[child.menu_name] ||
                                    activeMenu[child.menu_name]
                                      ? "white"
                                      : "inherit",
                                }}
                              >
                                {child.icon}
                              </Icon>
                            </ListItemIcon>
                            <Typography
                              sx={{
                                pl: 1,
                                fontSize: 16,
                                color:
                                  hovered[child.menu_name] ||
                                  activeMenu[child.menu_name]
                                    ? "white"
                                    : "inherit",
                              }}
                            >
                              {/* {i18n.language === "th"
                                ? child.menu_name_th
                                : child.menu_name_en} */}
                              {t(child.menu_name)}
                            </Typography>
                          </ListItemButton>
                        </Link>
                      ))}
                    </>
                  ) : (
                    <></>
                  )}
                </Collapse>
              </div>
            ))}
        </List>
      </Box>
    </Drawer>
  )
}

export default AdminSidebar
